import { Injectable, computed, signal } from '@angular/core';

/**
 * Estado de items "Fijados" del sidebar — el usuario puede pin/unpin items
 * frecuentes para tenerlos al tope sin importar qué grupo del menú los contiene.
 *
 * Persistencia: localStorage por usuario/empresa NO se hace acá — el storage es
 * global del navegador. Si en el futuro hay multi-usuario en el mismo browser,
 * habria que prefijar la key con el `userId` o `empresaId`. Por ahora un solo
 * scope alcanza (el CRM se usa típicamente con una sesión por device).
 *
 * Edge cases manejados:
 *   - Si un item pinned ya no aparece en el menú del backend (ej. permiso
 *     revocado), `cleanupAgainst()` lo descarta para no mostrar links rotos.
 *   - El localStorage puede estar bloqueado (modo incógnito de Safari, quota
 *     llena) — todas las escrituras van en try/catch. Si falla, el estado vive
 *     en memoria nomas, no rompe.
 *   - JSON corrupto en localStorage → reset a array vacío.
 */
export interface PinnedItem {
  /** Path canonico al que navega — sin queryParams. Es el ID estable. */
  path: string;
  /** Label mostrado en el sidebar. Cacheamos el último visto para no perder
   *  el nombre si el item desaparece momentáneamente del menú. */
  label: string;
  /** Nombre del icono legacy (input para <app-icon name="...">). Se cachea
   *  junto al label por el mismo motivo. */
  icon?: string;
  /** Timestamp del pin para ordenar de más reciente a más antiguo. */
  pinnedAt: number;
}

const STORAGE_KEY = 'crm.sidebar.pinned';
/** Limite suave — nada de scroll, el usuario ve todo de un vistazo. */
const MAX_PINNED = 8;

@Injectable({ providedIn: 'root' })
export class SidebarPinnedService {
  private _items = signal<PinnedItem[]>(this.loadFromStorage());

  /** Items pinned ordenados de más recientes a más antiguos. */
  readonly items = computed(() => [...this._items()].sort((a, b) => b.pinnedAt - a.pinnedAt));

  /** Cuántos items hay pinned — útil para mostrar/ocultar la sección. */
  readonly count = computed(() => this._items().length);

  /** Set de paths pinned para lookup O(1) en el HTML del menú. */
  readonly pathsSet = computed(() => new Set(this._items().map(p => p.path)));

  /** ¿Ya está pinned este path? */
  isPinned(path: string): boolean {
    return this.pathsSet().has(path);
  }

  /** Togglea pin/unpin de un item del menú. */
  toggle(item: { path: string; label: string; icon?: string }): void {
    if (!item.path) return;
    const current = this._items();
    if (this.isPinned(item.path)) {
      this._items.set(current.filter(p => p.path !== item.path));
    } else {
      const next: PinnedItem = {
        path: item.path,
        label: item.label,
        icon: item.icon,
        pinnedAt: Date.now(),
      };
      // Aplicar limite — si esta al maximo, descartamos el mas viejo.
      const sorted = [...current, next].sort((a, b) => b.pinnedAt - a.pinnedAt);
      this._items.set(sorted.slice(0, MAX_PINNED));
    }
    this.persist();
  }

  /** Saca todos los pinned (rara vez se usa — para tests o un boton "limpiar"). */
  clear(): void {
    this._items.set([]);
    this.persist();
  }

  /**
   * Limpia pinned items que ya no existen en el menú actual. Llamado por el
   * sidebar después de que `NavigationService` carga el menú. Evita links
   * rotos si el usuario perdió permiso a un módulo o el admin lo desactivó.
   *
   * @param validPaths Set de paths que SI existen en el menú actual.
   */
  cleanupAgainst(validPaths: Set<string>): void {
    const before = this._items();
    const after = before.filter(p => validPaths.has(p.path));
    if (after.length !== before.length) {
      this._items.set(after);
      this.persist();
    }
  }

  private loadFromStorage(): PinnedItem[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      // Validacion defensiva — descartar entries malformadas.
      return parsed.filter((p): p is PinnedItem =>
        p && typeof p.path === 'string'
        && typeof p.label === 'string'
        && typeof p.pinnedAt === 'number',
      ).slice(0, MAX_PINNED);
    } catch {
      return [];
    }
  }

  private persist(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this._items()));
    } catch {
      // localStorage bloqueado o quota llena — estado vive en memoria nomas.
    }
  }
}
